import {createContext, useState, useContext} from 'react';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {changeAntdTheme} from 'dynamic-antd-theme';

import isAuthenticated from '../../utils/Auth/isAuthenticated';
import {removeAll, setItem} from 'utils/Helper';
import AppIcon from 'components/AppComponents/AppIcon';
import {hasAccess} from 'utils/Helper';
// import {materialListUrl} from 'pages/MaterialManager';
import useSocket from 'hooks/useSocket';
import {useLogin} from 'api/Auth';

changeAntdTheme('#005eef');
export const AuthContext = createContext({});
export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({children}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {disconSocket, socket, connect} = useSocket();

  const {mutate: mutateLogin, isLoading: isLoging} = useLogin();
  const isLoading = isLoging;

  const [authUser, setAuthUser] = useState({
    isLoading: isLoading,
    isAuthenticated: isAuthenticated(),
    user: {
      role: 'user',
    },
  });

  const login = async (values) => {
    mutateLogin(
      {
        username: values.email,
        password: values.password,
      },
      {
        onSuccess: (res) => {
          const data = res?.data?.data;
          if (!data?.reset_stts || data.is_expired) {
            localStorage.setItem(
              'forgot-password-username',
              data?.username || values.email,
            );
            return navigate('/reset-password', {state: {msg: res?.data?.msg}});
          }
          setItem(localStorage, 'token', data.token);
          setItem(localStorage, 'org_info', JSON.stringify(data.org_info));
          setItem(localStorage, 'role', data?.role.toString());
          setItem(localStorage, 'isLoggedin', 'true');
          setItem(localStorage, 'username', data?.username);
          setItem(localStorage, 'user_id', data?.id.toString());
          setItem(localStorage, 'role_name', data?.role_name);
          setItem(localStorage, 'role_type', data?.role_type.toString());
          setItem(
            localStorage,
            'resource_access',
            data?.resource_access?.toString(),
          );
          setItem(localStorage, 'user_type', data?.user_type);
          setItem(localStorage, 'role_access', data?.role_access.toString());

          if (data?.agency_id) {
            setItem(localStorage, 'agency_id', data?.agency_id.toString());
          } else {
            setItem(localStorage, 'agency_id', '');
          }

          setAuthUser({
            isLoading: false,
            isAuthenticated: true,
            user: {
              displayName: data?.username,
              photoURL: 'https://unsplash.com/photos/iEEBWgY_6lA',
              role: 'user',
            },
          });
          connect();
        },
      },
    );
  };

  const logout = () => {
    disconSocket();

    removeAll(localStorage);
    setAuthUser({
      isLoading: false,
      isAuthenticated: false,
      user: {},
    });

    navigate('/signin');
  };

  const routeItems = [
    {
      id: 'user-manager',
      title: t('int-users'),
      icon: <AppIcon tag='mdi:user-supervisor' />,
      path: '/user-manager/user-list',
      access_code: 35,
    },
    {
      id: 'role-manager',
      title: t('int-role'),
      icon: <AppIcon tag='bxs:user-check' />,
      path: '/role-manager/role-list',
      access_code: 36,
    },
    {
      id: 'ff-manager',
      title: t('int-ff'),
      icon: <AppIcon tag='fa-solid:users' />,
      path: '/ff-manager/ff-list',
      access_code: 105,
    },
    {
      id: 'product-manager',
      title: t('int-product'),
      icon: <AppIcon tag='fa6-solid:box-open' />,
      path: '/product-manager/product-list',
      access_code: 151, //83
    },
    {
      id: 'campaign-manager',
      title: t('int-campaign'),
      icon: <AppIcon tag='material-symbols:campaign' />,
      path: '/campaign-manager/campaign-list',
      access_code: 37, //74
    },
    // {
    //   id: 'material-manager',
    //   title: t('int-material'),
    //   icon: <AppIcon tag='mdi:gift' />,
    //   path: materialListUrl,
    //   access_code: 163,
    // },
    {
      id: 'hr-manager',
      title: t('int-hr'),
      icon: <AppIcon tag='fa-solid:users-cog' />,
      path: '/hr-manager/hr-dashboard',
      access_code: 129,
    },
    {
      id: 'notification-manager',
      title: t('int-notifications'),
      icon: <AppIcon tag='ri:user-settings-fill' />,
      path: '/notification-manager/notification-list',
      access_code: 39,
    },
    {
      id: 'agency-manager',
      title: t('int-affiliates'),
      icon: <AppIcon tag='ri:home-gear-fill' />,
      path: '/agency-manager/agency-list',
      access_code: 188,
    },
    {
      id: 'team-manager',
      title: t('int-team-manager'),
      icon: <AppIcon tag='fluent:people-team-toolbox-20-filled' />,
      path: '/team-manager/team-list',
      access_code: 188,
    },
    {
      id: 'document-manager',
      title: 'Document Manager',
      icon: <AppIcon tag='mingcute:document-fill' />,
      path: '/document-manager/document-list',
      access_code: 188,
    },
    {
      id: 'retail-manager',
      title: t('int-retail'),
      icon: <AppIcon tag='fa6-solid:shop' />,
      path: '/retail-manager/landing',
      access_code: 85,
    },
    {
      id: 'organization-manager',
      title: t('int-organization'),
      icon: <AppIcon tag='ci:settings-filled' />,
      path: '/organization-manager/settings',
      access_code: 38,
    },
  ];
  let hasAccessRoutes = [
    {
      id: 'dashboard',
      title: t('int-home'),
      icon: <AppIcon tag='ant-design:home-filled' />,
      path: '/dashboard/home',
    },
  ];
  routeItems.map(
    (el) => hasAccess(el?.access_code) && hasAccessRoutes.push(el),
  );

  return (
    <AuthContext.Provider
      value={{
        authUser,
        login,
        logout,
        routesConfig: hasAccessRoutes,
        isLoading,
        socket,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
